/**
 * Channel Event Classification for Frontend
 *
 * Maps unified event enum values to translation keys.
 * Only ACTIVE events (those that affect the online/public situation of entities)
 * are delivered from the backend. This file provides the enum and translation
 * lookup for display in charts and event lists.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Unified Event Enum — mirrors backend channelEventClassification.js
// ─────────────────────────────────────────────────────────────────────────────

export const CHANNEL_EVENT_ENUM = {
    // Shopify – Product
    SHOPIFY_PRODUCT_UPDATED: 'shopify.product.updated',
    SHOPIFY_PRODUCT_DESTROYED: 'shopify.product.destroyed',
    SHOPIFY_PRODUCT_PUBLISHED: 'shopify.product.published',
    SHOPIFY_PRODUCT_UNPUBLISHED: 'shopify.product.unpublished',
    SHOPIFY_PRODUCT_STATUS_CHANGED: 'shopify.product.status_changed',

    // Shopify – Order
    SHOPIFY_ORDER_PLACED: 'shopify.order.placed',
    SHOPIFY_ORDER_CONFIRMED: 'shopify.order.confirmed',
    SHOPIFY_ORDER_CANCELLED: 'shopify.order.cancelled',
    SHOPIFY_ORDER_CLOSED: 'shopify.order.closed',
    SHOPIFY_ORDER_REOPENED: 'shopify.order.reopened',
    SHOPIFY_ORDER_FULFILLED: 'shopify.order.fulfilled',
    SHOPIFY_ORDER_FULFILLMENT_CANCELLED: 'shopify.order.fulfillment_cancelled',
    SHOPIFY_ORDER_REFUND_SUCCESS: 'shopify.order.refund_success',

    // Shopify – Collection
    SHOPIFY_COLLECTION_UPDATED: 'shopify.collection.updated',
    SHOPIFY_COLLECTION_DESTROYED: 'shopify.collection.destroyed',
    SHOPIFY_COLLECTION_PUBLISHED: 'shopify.collection.published',
    SHOPIFY_COLLECTION_UNPUBLISHED: 'shopify.collection.unpublished',

    // Shopify – Content
    SHOPIFY_ARTICLE_UPDATED: 'shopify.article.updated',
    SHOPIFY_ARTICLE_DESTROYED: 'shopify.article.destroyed',
    SHOPIFY_BLOG_UPDATED: 'shopify.blog.updated',
    SHOPIFY_BLOG_DESTROYED: 'shopify.blog.destroyed',
    SHOPIFY_PAGE_UPDATED: 'shopify.page.updated',
    SHOPIFY_PAGE_DESTROYED: 'shopify.page.destroyed',

    // Shopify – Price Rule (Discounts)
    SHOPIFY_PRICE_RULE_UPDATED: 'shopify.price_rule.updated',
    SHOPIFY_PRICE_RULE_DESTROYED: 'shopify.price_rule.destroyed',

    // Meta – Campaign
    META_CAMPAIGN_STATUS_UPDATED: 'meta.campaign.status_updated',
    META_CAMPAIGN_BUDGET_UPDATED: 'meta.campaign.budget_updated',
    META_CAMPAIGN_SCHEDULE_UPDATED: 'meta.campaign.schedule_updated',
    META_CAMPAIGN_ENDED: 'meta.campaign.ended',
    META_CAMPAIGN_SPEND_CAP_UPDATED: 'meta.campaign.spend_cap_updated',

    // Meta – Ad Set
    META_ADSET_STATUS_UPDATED: 'meta.adset.status_updated',
    META_ADSET_BUDGET_UPDATED: 'meta.adset.budget_updated',
    META_ADSET_BIDDING_UPDATED: 'meta.adset.bidding_updated',
    META_ADSET_BID_STRATEGY_UPDATED: 'meta.adset.bid_strategy_updated',
    META_ADSET_TARGETING_UPDATED: 'meta.adset.targeting_updated',
    META_ADSET_SCHEDULE_UPDATED: 'meta.adset.schedule_updated',

    // Meta – Ad
    META_AD_STATUS_UPDATED: 'meta.ad.status_updated',
    META_AD_CREATIVE_UPDATED: 'meta.ad.creative_updated',
    META_AD_REVIEW_APPROVED: 'meta.ad.review_approved',
    META_AD_REVIEW_DECLINED: 'meta.ad.review_declined',

    // Meta – Account
    META_ACCOUNT_STATUS_UPDATED: 'meta.account.status_updated',
    META_ACCOUNT_SPEND_LIMIT_UPDATED: 'meta.account.spend_limit_updated',
    META_ACCOUNT_SPEND_LIMIT_RESET: 'meta.account.spend_limit_reset',
    META_ACCOUNT_SPEND_LIMIT_REMOVED: 'meta.account.spend_limit_removed',
} as const;

export type ChannelEventEnum = typeof CHANNEL_EVENT_ENUM[keyof typeof CHANNEL_EVENT_ENUM];

// ─────────────────────────────────────────────────────────────────────────────
// Translation key mapping  (i18n key = `channelEvents.{enumValue}`)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resolve a raw eventType from the backend to a human-readable label
 * using the i18n translation function.
 *
 * Falls back to the raw eventType formatted as title case if no
 * translation is found.
 *
 * @param eventType – the raw eventType string from the API (e.g. "published", "update_campaign_budget")
 * @param eventEnum – the unified eventEnum string (e.g. "shopify.product.published")
 * @param t – the i18n translation function
 * @returns translated label string
 */
export function getEventLabel(
    eventType: string | undefined,
    eventEnum: string | undefined,
    t: (key: string, options?: Record<string, unknown>) => string,
): string {
    // Try the enum-based key first
    if (eventEnum) {
        const key = `channelEvents.${eventEnum}`;
        const translated = t(key);
        if (translated !== key) return translated;
    }

    // Fallback: format the raw eventType as title case
    if (eventType) {
        return eventType
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    return t('channelEvents.unknown');
}

/**
 * Resolve an event's display name from the best available source.
 * Priority: eventEnum translation > description > eventType formatted > fallback
 */
export function resolveEventDisplayName(
    event: { eventEnum?: string; eventType?: string; description?: string; category?: string },
    t: (key: string) => string,
): string {
    // 1. Try enum translation
    if (event.eventEnum) {
        const key = `channelEvents.${event.eventEnum}`;
        const translated = t(key);
        if (translated !== key) return translated;
    }

    // 2. Use description if available
    if (event.description) return event.description;

    // 3. Format eventType
    if (event.eventType) {
        return event.eventType
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    // 4. Category fallback
    if (event.category) {
        return event.category
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
    }

    return t('channelEvents.unknown');
}
